import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  FlowArrow, Plus, Trash, CircleNotch, CheckCircle, XCircle, Lightning, Plugs, CaretDown, Key,
} from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const inputCls = "w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none transition-all";

const newId = () => `wf_${Math.random().toString(36).slice(2, 9)}`;

const TEMPLATES = [
  { name: "Slå upp orderstatus", description: "När kunden frågar var sin order är eller vill veta orderstatus.",
    params: [{ name: "ordernummer", label: "kundens ordernummer" }, { name: "email", label: "e-post för verifiering" }],
    response_message: "Din order har status: ..." },
  { name: "Påbörja en retur", description: "När kunden vill returnera en vara och få en returetikett.",
    params: [{ name: "ordernummer", label: "ordernummer" }, { name: "anledning", label: "anledning till retur" }],
    response_message: "Jag har påbörjat din retur och en returetikett skickas till din e-post." },
  { name: "Boka tid", description: "När kunden vill boka eller boka om en tid.",
    params: [{ name: "tidpunkt", label: "önskat datum och tid" }, { name: "tjanst", label: "typ av tjänst" }],
    response_message: "Din tid är bokad. Du får en bekräftelse via e-post." },
];

const CONNECTORS = [
  { connector: "freshdesk", action: "create_ticket", label: "Freshdesk · Skapa ärende",
    name: "Skapa Freshdesk-ärende", description: "När kunden vill skapa ett nytt supportärende.",
    params: [{ name: "subject", label: "ärenderubrik" }, { name: "description", label: "beskrivning av problemet" }, { name: "email", label: "kundens e-post" }],
    response_message: "Jag har skapat ett ärende åt dig — du får en bekräftelse via e-post." },
  { connector: "freshdesk", action: "lookup_ticket", label: "Freshdesk · Slå upp ärende",
    name: "Slå upp Freshdesk-ärende", description: "När kunden frågar om status på ett befintligt ärende.",
    params: [{ name: "ticket_id", label: "ärendenummer" }],
    response_message: "Ditt ärende har status: ..." },
  { connector: "zendesk", action: "create_ticket", label: "Zendesk · Skapa ärende",
    name: "Skapa Zendesk-ärende", description: "När kunden vill skapa ett nytt supportärende.",
    params: [{ name: "subject", label: "ärenderubrik" }, { name: "description", label: "beskrivning av problemet" }, { name: "email", label: "kundens e-post" }],
    response_message: "Jag har skapat ett ärende åt dig — du får en bekräftelse via e-post." },
  { connector: "zendesk", action: "lookup_ticket", label: "Zendesk · Slå upp ärende",
    name: "Slå upp Zendesk-ärende", description: "När kunden frågar om status på ett befintligt ärende.",
    params: [{ name: "ticket_id", label: "ärendenummer" }],
    response_message: "Ditt ärende har status: ..." },
];

const CONNECTOR_LABEL = { freshdesk: "Freshdesk", zendesk: "Zendesk" };

const STATUS_HINTS = {
  401: "Fel inloggning — kontrollera e-post och API-token.",
  403: "Token saknar behörighet i supportsystemet.",
  404: "Hittades inte — kontrollera att subdomänen är rätt stavad.",
  429: "För många anrop just nu — försök igen om en stund.",
};

export default function WorkflowBuilder({ agentId, agent, set }) {
  const workflows = agent.workflows || [];

  const update = (id, patch) =>
    set("workflows", workflows.map((w) => (w.id === id ? { ...w, ...patch } : w)));

  const addWorkflow = (tpl) => {
    const wf = {
      id: newId(), name: tpl?.name || "", description: tpl?.description || "",
      params: tpl?.params ? [...tpl.params] : [{ name: "", label: "" }],
      mode: "ask", webhook_url: "", response_message: tpl?.response_message || "", enabled: true,
      connector: tpl?.connector || "", action: tpl?.action || "",
    };
    set("workflows", [...workflows, wf]);
  };

  const remove = (id) => set("workflows", workflows.filter((w) => w.id !== id));

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-zinc-200 bg-gradient-to-br from-[#0033FF]/5 to-transparent p-5">
        <h3 className="font-display font-bold text-lg flex items-center gap-2">
          <FlowArrow size={20} weight="fill" className="text-[#0033FF]" /> Egna workflows
        </h3>
        <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
          Bygg egna åtgärder som kopplar agenten till dina system. Agenten samlar automatiskt in rätt information
          från kunden och anropar din tjänst (webhook) eller ditt supportsystem direkt. Lämna webhook tom för att simulera medan du testar.
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          <button data-testid="add-workflow-blank" onClick={() => addWorkflow(null)}
            className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-4 py-2 font-semibold text-sm">
            <Plus size={15} weight="bold" /> Ny workflow
          </button>
          {TEMPLATES.map((t) => (
            <button key={t.name} data-testid={`add-workflow-tpl-${t.name}`} onClick={() => addWorkflow(t)}
              className="inline-flex items-center gap-1.5 bg-white border border-zinc-200 hover:border-[#0033FF] hover:text-[#0033FF] text-zinc-600 rounded-md px-3 py-2 font-semibold text-sm transition-colors">
              <Lightning size={14} weight="fill" /> {t.name}
            </button>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-zinc-200/70">
          <div className="text-xs font-bold uppercase tracking-wide text-zinc-400 mb-2 flex items-center gap-1.5">
            <Plugs size={13} weight="bold" /> Koppla ditt supportsystem (inget eget webhook behövs)
          </div>
          <div className="flex flex-wrap gap-2">
            {CONNECTORS.map((c) => (
              <button key={c.label} data-testid={`add-workflow-connector-${c.connector}-${c.action}`} onClick={() => addWorkflow(c)}
                className="inline-flex items-center gap-1.5 bg-white border border-zinc-200 hover:border-[#0033FF] hover:text-[#0033FF] text-zinc-600 rounded-md px-3 py-2 font-semibold text-sm transition-colors">
                <Plugs size={14} weight="fill" /> {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {workflows.length === 0 ? (
        <div data-testid="workflows-empty" className="text-center py-12 text-zinc-400 border border-dashed border-zinc-200 rounded-xl">
          <FlowArrow size={32} weight="duotone" className="mx-auto mb-2 text-zinc-300" />
          <p className="text-sm">Inga workflows än. Skapa din första ovan — t.ex. ”Slå upp orderstatus”.</p>
        </div>
      ) : (
        workflows.map((w) => (
          <WorkflowCard key={w.id} agentId={agentId} wf={w} update={update} remove={remove} />
        ))
      )}
    </div>
  );
}

function WorkflowCard({ agentId, wf, update, remove }) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [showAdv, setShowAdv] = useState(false);
  const isConnector = !!wf.connector;

  const setParam = (i, patch) => {
    const params = (wf.params || []).map((p, idx) => (idx === i ? { ...p, ...patch } : p));
    update(wf.id, { params });
  };
  const addParam = () => update(wf.id, { params: [...(wf.params || []), { name: "", label: "" }] });
  const removeParam = (i) => update(wf.id, { params: (wf.params || []).filter((_, idx) => idx !== i) });

  const headers = wf.headers || [];
  const setHeader = (i, patch) => update(wf.id, { headers: headers.map((h, idx) => (idx === i ? { ...h, ...patch } : h)) });
  const addHeader = () => update(wf.id, { headers: [...headers, { key: "", value: "" }] });
  const removeHeader = (i) => update(wf.id, { headers: headers.filter((_, idx) => idx !== i) });

  const runTest = async () => {
    if (!wf.webhook_url?.trim()) return toast.error("Ange en URL att testa");
    setTesting(true);
    setTestResult(null);
    try {
      const sample = {};
      (wf.params || []).forEach((p) => { if (p.name) sample[p.name] = "test"; });
      const { data } = await api.post(`/agents/${agentId}/workflows/test`, {
        webhook_url: wf.webhook_url.trim(), sample_args: sample, tool: wf.id,
        http_method: wf.http_method || "POST", headers: (wf.headers || []).filter((h) => h.key?.trim()),
        body_mode: wf.body_mode || "auto", body_template: wf.body_template || "",
      });
      setTestResult({ ok: data.ok, status: data.status, body: data.result });
      data.ok ? toast.success(`Tjänsten svarade (${data.status})`) : toast.error(`Tjänsten svarade ${data.status}`);
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Kunde inte nå tjänsten" });
      toast.error(e?.response?.data?.detail || "Kunde inte nå tjänsten");
    } finally { setTesting(false); }
  };

  const verifyConnector = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const creds = {
        fd_domain: wf.fd_domain || "", fd_api_key: wf.fd_api_key || "",
        zd_subdomain: wf.zd_subdomain || "", zd_email: wf.zd_email || "", zd_api_token: wf.zd_api_token || "",
      };
      const { data } = await api.post(`/agents/${agentId}/workflows/verify-connector`, { connector: wf.connector, creds });
      setVerifyResult(data);
      data.ok ? toast.success(`Ansluten${data.name ? ` som ${data.name}` : ""}`) : toast.error(`Anslutningen misslyckades (${data.status || ""})`);
    } catch (e) {
      setVerifyResult({ ok: false, detail: e?.response?.data?.detail || "Kunde inte verifiera" });
      toast.error(e?.response?.data?.detail || "Kunde inte verifiera anslutningen");
    } finally { setVerifying(false); }
  };

  return (
    <div data-testid={`workflow-card-${wf.id}`} className="rounded-xl border border-zinc-200 bg-white p-5 space-y-4">
      {isConnector && (
        <span data-testid="workflow-connector-badge" className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide px-2 py-1 rounded bg-[#0033FF]/10 text-[#0033FF]">
          <Plugs size={12} weight="fill" /> {CONNECTOR_LABEL[wf.connector] || wf.connector} · {wf.action === "create_ticket" ? "Skapa ärende" : wf.action === "lookup_ticket" ? "Slå upp ärende" : wf.action}
        </span>
      )}
      <div className="flex items-center gap-3">
        <input data-testid="workflow-name" value={wf.name} onChange={(e) => update(wf.id, { name: e.target.value })}
          placeholder="Workflow-namn (t.ex. Slå upp orderstatus)" className={`${inputCls} font-semibold flex-1`} />
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-zinc-400">{wf.enabled ? "Aktiv" : "Av"}</span>
          <Switch data-testid="workflow-enabled" checked={!!wf.enabled} onCheckedChange={(v) => update(wf.id, { enabled: v })} />
        </div>
        <button data-testid="workflow-delete" onClick={() => remove(wf.id)}
          className="p-2 rounded-md hover:bg-red-50 text-zinc-400 hover:text-red-600 shrink-0"><Trash size={16} weight="bold" /></button>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">När ska agenten använda den?</label>
        <textarea data-testid="workflow-description" rows={2} value={wf.description} maxLength={500}
          onChange={(e) => update(wf.id, { description: e.target.value })}
          placeholder="Beskriv i klartext, t.ex. ”När kunden frågar var sin order är.”" className={`${inputCls} mt-1`} />
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Information agenten samlar in från kunden</label>
        <div className="space-y-2 mt-1.5">
          {(wf.params || []).map((p, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input data-testid="workflow-param-name" value={p.name} onChange={(e) => setParam(i, { name: e.target.value })}
                placeholder="fält (t.ex. ordernummer)" className={`${inputCls} flex-1`} />
              <input data-testid="workflow-param-label" value={p.label} onChange={(e) => setParam(i, { label: e.target.value })}
                placeholder="beskrivning för agenten" className={`${inputCls} flex-1`} />
              <button data-testid="workflow-param-remove" onClick={() => removeParam(i)}
                className="p-2 rounded-md hover:bg-red-50 text-zinc-300 hover:text-red-500 shrink-0"><XCircle size={18} weight="fill" /></button>
            </div>
          ))}
          <button data-testid="workflow-param-add" onClick={addParam}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0033FF] hover:underline">
            <Plus size={14} weight="bold" /> Lägg till fält
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Behörighet</label>
          <Select value={wf.mode || "ask"} onValueChange={(v) => update(wf.id, { mode: v })}>
            <SelectTrigger className="mt-1" data-testid="workflow-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ask">Fråga kunden först</SelectItem>
              <SelectItem value="auto">Kör automatiskt</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-zinc-400 mt-1">”Fråga först” = agenten ber om kundens bekräftelse innan åtgärden körs. ”Automatiskt” = körs direkt (passar ofarliga uppslag).</p>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Svar till kunden efter körning (valfritt)</label>
          <input data-testid="workflow-response" value={wf.response_message || ""} maxLength={300}
            onChange={(e) => update(wf.id, { response_message: e.target.value })}
            placeholder="t.ex. Din retur är påbörjad – etikett skickas till din e-post." className={`${inputCls} mt-1`} />
        </div>
      </div>

      <div>
        {isConnector ? (
          <>
            <label className="text-xs font-bold uppercase tracking-wide text-zinc-400 flex items-center gap-1.5">
              <Plugs size={13} weight="bold" /> Anslutning till {CONNECTOR_LABEL[wf.connector] || wf.connector}
            </label>
            <div className="grid sm:grid-cols-2 gap-2 mt-1.5">
              {wf.connector === "freshdesk" ? (
                <>
                  <input data-testid="workflow-fd-domain" value={wf.fd_domain || ""} onChange={(e) => update(wf.id, { fd_domain: e.target.value })}
                    placeholder="Subdomän (t.ex. dittforetag)" className={inputCls} />
                  <input data-testid="workflow-fd-key" type="password" value={wf.fd_api_key || ""} onChange={(e) => update(wf.id, { fd_api_key: e.target.value })}
                    placeholder="Freshdesk API-nyckel" className={inputCls} />
                </>
              ) : (
                <>
                  <input data-testid="workflow-zd-subdomain" value={wf.zd_subdomain || ""} onChange={(e) => update(wf.id, { zd_subdomain: e.target.value })}
                    placeholder="Subdomän (t.ex. dittforetag)" className={inputCls} />
                  <input data-testid="workflow-zd-email" value={wf.zd_email || ""} onChange={(e) => update(wf.id, { zd_email: e.target.value })}
                    placeholder="Agent-e-post" className={inputCls} />
                  <input data-testid="workflow-zd-token" type="password" value={wf.zd_api_token || ""} onChange={(e) => update(wf.id, { zd_api_token: e.target.value })}
                    placeholder="Zendesk API-token" className={`${inputCls} sm:col-span-2`} />
                </>
              )}
            </div>
            <button data-testid="workflow-verify" onClick={verifyConnector} disabled={verifying}
              className="inline-flex items-center gap-2 mt-2 bg-white border border-zinc-200 hover:bg-zinc-50 rounded-md px-4 py-2 font-semibold text-sm disabled:opacity-50">
              {verifying ? <CircleNotch size={15} className="animate-spin" /> : <Plugs size={15} weight="fill" />} Verifiera anslutning
            </button>
            {verifyResult && (
              <div data-testid="workflow-verify-result" className={`mt-2 text-xs rounded-md p-2.5 flex items-start gap-2 ${verifyResult.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                {verifyResult.ok ? <CheckCircle size={15} weight="fill" className="mt-0.5 shrink-0" /> : <XCircle size={15} weight="fill" className="mt-0.5 shrink-0" />}
                <span>{verifyResult.ok ? `Ansluten${verifyResult.name ? ` som ${verifyResult.name}` : ""} ✓` : (STATUS_HINTS[verifyResult.status] || verifyResult.detail || `Misslyckades (${verifyResult.status || ""})`)}</span>
              </div>
            )}
            <p className="text-xs text-zinc-400 mt-1.5">Agenten utför åtgärden direkt i {CONNECTOR_LABEL[wf.connector] || wf.connector} — inget eget webhook behövs. Uppgifterna lagras säkert på agenten.</p>
          </>
        ) : (
          <>
            <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Anrop till ditt system</label>
            <div className="flex gap-2 mt-1">
              <Select value={wf.http_method || "POST"} onValueChange={(v) => update(wf.id, { http_method: v })}>
                <SelectTrigger className="w-[110px] shrink-0 font-mono text-xs" data-testid="workflow-http-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <input data-testid="workflow-webhook" value={wf.webhook_url || ""} onChange={(e) => update(wf.id, { webhook_url: e.target.value })}
                placeholder="https://ditt-system.se/api/order/{{ordernummer}} — lämna tom för simulering" className={`${inputCls} flex-1 font-mono text-xs`} />
              <button data-testid="workflow-test" onClick={runTest} disabled={testing}
                className="inline-flex items-center gap-2 bg-white border border-zinc-200 hover:bg-zinc-50 rounded-md px-4 py-2 font-semibold text-sm disabled:opacity-50 whitespace-nowrap">
                {testing ? <CircleNotch size={15} className="animate-spin" /> : <Lightning size={15} weight="fill" />} Testa
              </button>
            </div>
            <p className="text-xs text-zinc-400 mt-1.5">Använd <code className="font-mono bg-zinc-100 px-1 rounded">{`{{fältnamn}}`}</code> var som helst i URL, headers eller body för att fylla i kundens uppgifter. Tom URL = agenten simulerar åtgärden.</p>

            <button data-testid="workflow-adv-toggle" onClick={() => setShowAdv((s) => !s)}
              className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold uppercase tracking-wide text-zinc-500 hover:text-[#0033FF]">
              <CaretDown size={13} weight="bold" className={`transition-transform ${showAdv ? "rotate-180" : ""}`} /> Avancerat — headers & body
            </button>

            {showAdv && (
              <div data-testid="workflow-adv-section" className="mt-3 space-y-4 border-l-2 border-[#0033FF]/20 pl-4">
                {/* headers */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-zinc-400 flex items-center gap-1.5">
                    <Key size={13} weight="bold" /> Headers (t.ex. Authorization)
                  </label>
                  <div className="space-y-2 mt-1.5">
                    {headers.map((h, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input data-testid="workflow-header-key" value={h.key || ""} onChange={(e) => setHeader(i, { key: e.target.value })}
                          placeholder="Header (t.ex. Authorization)" className={`${inputCls} flex-1 font-mono text-xs`} />
                        <input data-testid="workflow-header-value" value={h.value || ""} onChange={(e) => setHeader(i, { value: e.target.value })}
                          placeholder="Värde (t.ex. Bearer abc123)" className={`${inputCls} flex-1 font-mono text-xs`} />
                        <button data-testid="workflow-header-remove" onClick={() => removeHeader(i)}
                          className="p-2 rounded-md hover:bg-red-50 text-zinc-300 hover:text-red-500 shrink-0"><XCircle size={18} weight="fill" /></button>
                      </div>
                    ))}
                    <button data-testid="workflow-header-add" onClick={addHeader}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0033FF] hover:underline">
                      <Plus size={14} weight="bold" /> Lägg till header
                    </button>
                  </div>
                </div>

                {/* body */}
                {(wf.http_method || "POST") !== "GET" && (wf.http_method || "POST") !== "DELETE" && (
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Request body</label>
                    <Select value={wf.body_mode || "auto"} onValueChange={(v) => update(wf.id, { body_mode: v })}>
                      <SelectTrigger className="mt-1" data-testid="workflow-body-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Automatisk — {`{tool, args, agent_id}`}</SelectItem>
                        <SelectItem value="custom">Egen JSON-mall</SelectItem>
                        <SelectItem value="none">Ingen body</SelectItem>
                      </SelectContent>
                    </Select>
                    {(wf.body_mode === "custom") && (
                      <textarea data-testid="workflow-body-template" rows={4} value={wf.body_template || ""}
                        onChange={(e) => update(wf.id, { body_template: e.target.value })}
                        placeholder={`{\n  "order": "{{ordernummer}}",\n  "email": "{{email}}"\n}`}
                        className={`${inputCls} mt-2 font-mono text-xs`} />
                    )}
                  </div>
                )}
              </div>
            )}

            {testResult && (
              <div data-testid="workflow-test-result" className={`mt-2 text-xs rounded-md p-2.5 flex items-start gap-2 ${testResult.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                {testResult.ok ? <CheckCircle size={15} weight="fill" className="mt-0.5 shrink-0" /> : <XCircle size={15} weight="fill" className="mt-0.5 shrink-0" />}
                <span className="font-mono break-all">{testResult.error || `HTTP ${testResult.status} · ${JSON.stringify(testResult.body).slice(0, 200)}`}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
